// @ts-strict-ignore
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import apiUrls from "src/api";
import { prettifyErrorMessage } from "src/common/util/prettifyErrorMessage";
import {
  collectGraphIssues,
  groupPldsByContentId,
} from "src/features/graph/util/collectGraphIssues";
import type { FlatGraphResponse } from "src/features/graph/util/dataTypes/FlatGraphResponse";
import { buildGraph } from "src/features/graph/util/graphBuilder";
import { formatContentTypeName } from "src/features/graph/util/NameFormatter";
import Spinner from "../Loader/Spinner";
import { Tooltip } from "../Tooltip/Tooltip";
import type { CardIssue } from "./CardIssuesPanel";
import CardIssuesPanel from "./CardIssuesPanel";

export interface SelectableCard {
  component: any;
  id: string;
  disabled?: boolean;
  selected?: boolean;
}

export interface ICardsSelectorProps {
  parentClassName?: string;
  cardClassName?: string;
  disabledTooltipText: string;
  cards: SelectableCard[];
  cardClickCb: any;
  shouldCheckProjectIntegrity?: boolean;
  /**
   * Deep link to a card's graph, focused on one issue. Enables the per-card
   * issues panel: the badge opens the same problems list as the graph page,
   * and clicking a row navigates there (contentId 0 = no node to focus).
   */
  issuesLinkFor?: (cardId: string, contentId: number) => string;
}

// Per-card diagnosis: `blockingMessages` = the project can't even be loaded
// (backend "not vrReady" 400 — same rule as before), `issues` = the SAME
// problems the graph page's panel lists (collectGraphIssues), plus the
// unlinked-scenes count the backend reports.
type CardDiagnosis = {
  projectId: string;
  blockingMessages: string[];
  issues: CardIssue[];
};

const CardsSelector = ({
  parentClassName = "flex items-center justify-center",
  cardClassName = "p-4 m-1 w-28 h-full border-2 cursor-pointer select-none",
  cards = [],
  cardClickCb = () => null,
  disabledTooltipText = "",
  shouldCheckProjectIntegrity = false,
  issuesLinkFor,
}: ICardsSelectorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isFinishedCheckingValidity, setIsFinishedCheckingValidity] = useState(false);
  const [diagnosisByProjectId, setDiagnosisByProjectId] = useState<CardDiagnosis[]>([]);
  const [issuesPanelProjectId, setIssuesPanelProjectId] = useState<string | null>(null);

  const computeStyle = (card: SelectableCard, isBlocked: boolean) => {
    if (isBlocked || card.disabled) {
      return "cursor-not-allowed opacity-70"; // Lighter grayed-out effect
    } else if (card.selected) {
      return "border-green-500";
    } else {
      return "border-gray-500";
    }
  };

  useEffect(() => {
    if (shouldCheckProjectIntegrity) {
      const fetchValidity = async () => {
        const results = await Promise.all(
          cards.map(async (card): Promise<CardDiagnosis> => {
            try {
              const res = await axios.get<FlatGraphResponse>(
                `${apiUrls.projects.graph.flat}/${card.id}?isForGraph=true`,
              );
              const flat = res.data;
              const [nodes, , , hiddenScenes] = buildGraph(
                flat.contents ?? [],
                flat.contentTypes ?? [],
                flat.actions ?? [],
              );
              const issues: CardIssue[] = collectGraphIssues(
                nodes,
                groupPldsByContentId(flat.projectLanguageDatas ?? []),
                flat.project?.enabled_languages ?? [],
              ).map(({ node, issue }) => ({
                key: `${node.type}-${node.contentTypeId}-${issue.messageKey}-${
                  issue.messageParams?.language ?? ""
                }`,
                severity: issue.severity,
                messageKey: issue.messageKey,
                messageParams: issue.messageParams,
                fieldKeys: issue.fieldKeys,
                nodeName: String(node?.details?.name ?? ""),
                typeLabel: formatContentTypeName(node.modelName ?? ""),
                contentId: Number(node.contentId),
              }));
              // Unlinked scenes live outside the displayed graph: list them in
              // their own section, one row per scene, so each click can focus
              // the scene in the graph's unlinked strip. Same source as the
              // graph page (buildGraph's hiddenScenes).
              for (const hsn of hiddenScenes ?? []) {
                issues.push({
                  key: `unlinked-scene-${hsn.contentId}`,
                  severity: "warning",
                  section: "unlinked",
                  messageKey: "unlinkedScene",
                  nodeName: String(hsn?.details?.name ?? ""),
                  typeLabel: formatContentTypeName("content-scene"),
                  contentId: Number(hsn.contentId),
                });
              }
              // Fallback: the backend flags unlinked scenes with a slightly
              // different rule (no incoming action vs. unreachable) — if it
              // sees some the local build didn't, keep the aggregate row.
              const backendUnlinkedCount =
                (flat.warningMessages ?? []).find((w) => w?.error === "unlinked_scenes")?.details
                  ?.unlinkedScenesIds?.length ?? 0;
              if ((hiddenScenes?.length ?? 0) === 0 && backendUnlinkedCount > 0) {
                issues.push({
                  key: "unlinked-scenes",
                  severity: "warning",
                  section: "unlinked",
                  messageKey: "unlinkedScenes",
                  messageParams: { count: backendUnlinkedCount },
                  nodeName: "",
                  typeLabel: formatContentTypeName("content-scene"),
                  contentId: 0,
                });
              }
              return { projectId: card.id, blockingMessages: [], issues };
            } catch (error: any) {
              // 400 = the backend refuses to build the graph (not vrReady,
              // broken relationships): unpublishable, card stays unselectable.
              const data = error?.response?.data?.error?.details ?? error?.response?.data ?? {};
              const messages = [
                ...(data.errorMessages ?? []),
                ...(data.message ? [data.message] : []),
              ].map(String);
              return {
                projectId: card.id,
                blockingMessages: messages.length ? messages : ["generic_getProject_failure"],
                issues: [],
              };
            }
          }),
        );
        setDiagnosisByProjectId(results);
        setIsFinishedCheckingValidity(true);
      };
      void fetchValidity();
    } else {
      setIsFinishedCheckingValidity(true);
    }
  }, [cards]);

  const buildBadgeTooltip = (diagnosis: CardDiagnosis) => {
    const errorCount = diagnosis.issues.filter((i) => i.severity === "error").length;
    const warningCount = diagnosis.issues.length - errorCount;
    const parts: string[] = [];
    if (errorCount > 0) {
      parts.push(t("pages.projectGroup.publish.moduleGraphErrors", { count: errorCount }));
    }
    if (warningCount > 0) {
      parts.push(t("pages.projectGroup.publish.moduleGraphWarnings", { count: warningCount }));
    }
    return `${parts.join(" · ")} — ${t("pages.projectGroup.publish.moduleGraphIssuesOpen")}`;
  };

  const panelDiagnosis = issuesPanelProjectId
    ? diagnosisByProjectId.find((d) => d.projectId === issuesPanelProjectId)
    : undefined;

  return (
    <>
      {!isFinishedCheckingValidity ? (
        <div className="flex items-center justify-center relative h-full">
          <Spinner />
        </div>
      ) : (
        <div className={parentClassName}>
          {cards.map((card: SelectableCard, i: number) => {
            const diagnosis = diagnosisByProjectId.find((d) => d.projectId === card.id);
            const isBlocked = (diagnosis?.blockingMessages.length ?? 0) > 0;
            const hasErrors = Boolean(diagnosis?.issues.some((iss) => iss.severity === "error"));
            const hasIssues = (diagnosis?.issues.length ?? 0) > 0;

            let badgeTooltip = "";
            if (isBlocked) {
              const blockedMessage = prettifyErrorMessage(diagnosis.blockingMessages.join(","));
              badgeTooltip = `${blockedMessage.msg}. ${blockedMessage.subMsg}`;
            } else if (hasIssues) {
              badgeTooltip = buildBadgeTooltip(diagnosis);
            }

            return (
              <Tooltip
                className={`${
                  card.disabled || isBlocked ? "cursor-not-allowed" : "cursor-pointer"
                }`}
                classNameChildren="left-0"
                forceSingleLine={false}
                key={`${i}=_${i}`}
                message={card.disabled && !isBlocked ? disabledTooltipText : ""} // Show tooltip only if disabled and no errors
              >
                <div
                  className={`${cardClassName} ${computeStyle(card, isBlocked)} w-full`}
                  onClick={() => !isBlocked && cardClickCb(card.id)}
                >
                  <div className="relative w-full">
                    {card.component}
                    {/* Same pill as the graph toolbar's problems button, so
                        the warning reads identically in both places. Opens
                        the same problems panel without leaving the flow. */}
                    {(isBlocked || hasIssues) && (
                      <button
                        type="button"
                        title={badgeTooltip.trim()}
                        className={`absolute z-50 top-2 right-2 flex items-center gap-1 px-2 py-1 border rounded-lg text-sm bg-white font-medium transition-colors ${
                          isBlocked || hasErrors
                            ? "border-red-400 text-red-600"
                            : "border-amber-400 text-amber-600"
                        } ${!isBlocked && hasIssues ? "cursor-pointer" : "cursor-default"}`}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                          event.stopPropagation();
                          if (!isBlocked && hasIssues && issuesLinkFor) {
                            setIssuesPanelProjectId(card.id);
                          }
                        }}
                      >
                        <span>⚠</span>
                        {hasIssues && <span>{diagnosis.issues.length}</span>}
                        {/* Rendered as a flipped ▸ so it matches the graph
                            badge's caret glyph exactly. */}
                        {!isBlocked && hasIssues && (
                          <span className="inline-block text-xs rotate-180">▸</span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}

      {panelDiagnosis && issuesLinkFor && (
        <CardIssuesPanel
          issues={panelDiagnosis.issues}
          onClose={() => setIssuesPanelProjectId(null)}
          onSelect={(issue) => navigate(issuesLinkFor(panelDiagnosis.projectId, issue.contentId))}
        />
      )}
    </>
  );
};

export default CardsSelector;
